import get from 'lodash/get';
import find from 'lodash/find';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import MKBox from 'components/MaterialKit/MKBox';
import DataTable from 'components/DataTable';
import { handleErrorResponse, parseJSON } from 'utils/general';
import MKTypography from 'components/MaterialKit/MKTypography';
import { deleteDatum, getData } from 'api/general';
import { useAuth } from 'contexts/auth';
import Image from 'components/Image';
import moment from 'moment';
import { DATE_FORMAT_DATE_ONLY_DEFAULT } from 'utils/datetime';
import { useNavigate } from 'react-router-dom';
import { withLoading } from 'utils/hoc';

const ConfigurationsTableSection = ({
  title,
  title_font_size,
  title_color,
  table_name,
  id_field,
  columns_config,
  column_mapping_config,
  value_mapping_config,
  odd_row_background_color,
  even_row_background_color,
  odd_row_font_color,
  even_row_font_color,
  header_background_color,
  header_font_color,
  page_options,
  extra_params,
  add_data_path,
  edit_data_path,
  addable,
  editable,
  deletable,
  selectable,
  support_pagination,
  show_data_count,
  container_props,
  setLoading,
  ...props
}) => {
  const { setAuth } = useAuth();
  const [rowsData, setRowsData] = useState([]);

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [intermediateKeyword, setIntermediateKeyword] = useState('');
  const [keyword, setKeyword] = useState('');

  const [formattedTableColumns, setFormattedTableColumns] = useState([]);
  const navigate = useNavigate();

  const fetchConfigurationsFromApi = useCallback(() => {
    setLoading(true);
    const dataParams = {
      ...extra_params,
      ...(sortBy ? {
        $orderBy: `${sortBy} ${sortDirection}`,
      } : {}),
      $skip: currentPage * pageSize,
      $top: pageSize,
    };
    if (keyword) {
      dataParams.keyword = keyword;
    }
    return getData(`/${table_name}`, dataParams, 'bearer')
      .then(({ data, headers }) => {
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        setRowsData(data);
        setTotalRows(size);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      }).finally(() => {
        setLoading(false);
      });
  }, [currentPage, extra_params, pageSize, setAuth, setLoading, sortBy, sortDirection, table_name, keyword]);

  useEffect(() => {
    if (table_name) {
      fetchConfigurationsFromApi();
    }
  }, [fetchConfigurationsFromApi, table_name]);

  const onClickSort = useCallback((headerName) => {
    const column = find(formattedTableColumns, ['field', headerName]);
    if (column && !column.is_non_sortable) {
      if (sortBy === headerName) {
        setSortDirection((previousSortDirection) => (previousSortDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(headerName);
        setSortDirection('asc');
      }
    }
  }, [formattedTableColumns, sortBy]);

  const handleKeywordChange = (evt) => {
    setIntermediateKeyword(evt.target.value);
  }
  const handleKeywordKeyUp = (evt) => {
    if (evt.code === 'Enter') {
      setKeyword(intermediateKeyword);
      setCurrentPage(0);
    }
  }
  const handleKeywordBlur = (evt) => {
    setKeyword(intermediateKeyword);
    setCurrentPage(0);
  }

  useEffect(() => {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const columnName in value_mapping_config) {
      // eslint-disable-next-line no-loop-func
      (columns_config || []).forEach((column) => {
        if (column.field === columnName) {
          // eslint-disable-next-line no-param-reassign
          column.renderContent = (data) => {
            const type = value_mapping_config[columnName]?.type;
            if (data[columnName] || data[columnName] !== null) {
              const value = data[columnName];
              const mappingConfig = value_mapping_config[columnName];
              const foreignPath = mappingConfig?.path;
              const foreignCollectionSeparator = mappingConfig?.separator;
              return (
                <MKBox display="flex" justifyContent={value_mapping_config[columnName]?.justifyContent || 'center'} flexDirection="row" alignItems="center">
                  {type && type.includes('image') && (
                    <Image
                      width={value_mapping_config[columnName]?.image_width}
                      src={value_mapping_config[columnName][`${data[columnName]}_image`] || value_mapping_config[columnName]?.image}
                      style={{ marginRight: '0.25rem' }}
                      {...(type.includes('url') && {
                        onClick: () => window.open(data[columnName], '_blank').focus(),
                        style: { cursor: 'pointer' },
                      })}
                    />
                  )}
                  {type && type.includes('string') && (
                    <MKTypography
                      fontSize="0.875rem"
                      color={value_mapping_config[columnName][`${data[columnName]}_color`] || value_mapping_config[columnName]?.color}
                      {...(type.includes('navigate') && {
                        onClick: (e) => {
                          navigate(`${value_mapping_config[columnName]?.to}?${id_field}=${data[id_field]}`);
                          e.stopPropagation();
                        },
                        style: { cursor: 'pointer' },
                      })}
                      textAlign={value_mapping_config[columnName]?.textAlign || 'center'}
                    >
                      {value_mapping_config[columnName][data[columnName]] || data[columnName]}
                    </MKTypography>
                  )}
                  {type && type.includes('boolean') && (
                    <MKTypography
                      fontSize="0.875rem"
                      textAlign={value_mapping_config[columnName]?.textAlign || 'center'}
                      color={value_mapping_config[columnName][`${data[columnName]}_color`] || value_mapping_config[columnName]?.color}
                    >
                      {value_mapping_config[columnName][data[columnName]] || data[columnName]}
                    </MKTypography>
                  )}
                  {type && type.includes('timestamp') && (
                    <MKTypography
                      fontSize="0.875rem"
                      color={value_mapping_config[columnName]?.color}
                      textAlign={value_mapping_config[columnName]?.textAlign || 'center'}
                    >
                      {moment(data[columnName]).format(value_mapping_config[columnName]?.format || DATE_FORMAT_DATE_ONLY_DEFAULT)}
                    </MKTypography>
                  )}
                  {type && type.includes('foreign_collection_count') && (
                    <MKTypography
                      fontSize="0.875rem"
                      color={value_mapping_config[columnName]?.color}
                      textAlign={value_mapping_config[columnName]?.textAlign || 'center'}
                    >
                      {data[columnName]?.length}
                    </MKTypography>
                  )}
                  {type && type.includes('foreign_object_concat') && (
                    <MKTypography
                      fontSize="0.875rem"
                      color={mappingConfig?.color}
                      textAlign={mappingConfig?.textAlign || 'center'}
                    >
                      {foreignPath == null || foreignPath === '' ? value : get(value, foreignPath)}
                    </MKTypography>
                  )}
                  {type && type.includes('foreign_collection_concat') && (
                    <MKTypography
                      fontSize="0.875rem"
                      color={mappingConfig?.color}
                      textAlign={mappingConfig?.textAlign || 'center'}
                    >
                      {value && value.map((datum) => {
                        return foreignPath == null || foreignPath === '' ? datum : get(datum, foreignPath);
                      }).join(foreignCollectionSeparator == null ? '' : foreignCollectionSeparator)}
                    </MKTypography>
                  )}
                </MKBox>
              );
            }
            return ' ';
          };
        }
      });
    }
    setFormattedTableColumns(columns_config || []);
  }, [columns_config, id_field, navigate, value_mapping_config]);

  const deleteFromApi = useCallback((dataId) => {
    // setLoading(true);
    return deleteDatum(`/${table_name}`, dataId, 'bearer')
      .then(() => {
        return fetchConfigurationsFromApi();
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [table_name, fetchConfigurationsFromApi, setAuth]);

  const onClickAdd = useCallback(() => {
    if (add_data_path) {
      navigate(add_data_path);
    }
  }, [add_data_path, navigate]);

  const onClickEdit = useCallback((dataId) => {
    if (edit_data_path) {
      navigate(`${edit_data_path}?${id_field}=${dataId}`);
    }
  }, [id_field, edit_data_path, navigate]);

  return (
    <MKBox
      sx={{
        ...parseJSON(container_props, {}),
      }}
      {...props}
    >
      <MKBox mb={2} display="flex" alignItems="center">
        <MKTypography mr="1rem" color={title_color} fontSize={title_font_size}>
          {`${title} ${show_data_count ? `(${totalRows})` : ''}`}
        </MKTypography>
      </MKBox>

      {table_name === 'contacts' && (
        <MKBox display="flex" bgColor="white" width="100%" alignItems="center" style={{ borderRadius: 10 }}>
          <SearchIcon fontSize="medium" style={{ marginLeft: '1rem', marginRight: '1rem' }} />
          <TextField
            placeholder="Search by keyword"
            onChange={handleKeywordChange}
            value={intermediateKeyword}
            inputProps={{
              onKeyUp: handleKeywordKeyUp,
              onBlur: handleKeywordBlur,
            }}
          />
        </MKBox>
      )}

      <DataTable
        data={rowsData}
        columns={formattedTableColumns}
        headerBackgroundColor={header_background_color}
        headerFontColor={header_font_color}
        oddRowBackgroundColor={odd_row_background_color}
        oddRowFontColor={odd_row_font_color}
        evenRowBackgroundColor={even_row_background_color}
        evenRowFontColor={even_row_font_color}
        idField={id_field}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={onClickSort}
        paginationDisabled={!support_pagination}
        onClickRow={editable && selectable ? onClickEdit : null}
        onPressAdd={addable && selectable ? onClickAdd : null}
        onPressEdit={editable && selectable ? onClickEdit : null}
        onPressDelete={deletable && selectable ? deleteFromApi : null}
        selectable={selectable}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={page_options}
        totalCount={totalRows}
        onRowsPerPageChange={setPageSize}
      />
    </MKBox>
  );
};

ConfigurationsTableSection.propTypes = {
  title: PropTypes.string,
  title_font_size: PropTypes.string,
  title_color: PropTypes.string,
  table_name: PropTypes.string,
  id_field: PropTypes.string,
  user_relation_table_name: PropTypes.string,
  columns_config: PropTypes.array,
  column_mapping_config: PropTypes.array,
  value_mapping_config: PropTypes.object,
  odd_row_background_color: PropTypes.string,
  even_row_background_color: PropTypes.string,
  odd_row_font_color: PropTypes.string,
  even_row_font_color: PropTypes.string,
  header_background_color: PropTypes.string,
  header_font_color: PropTypes.string,
  page_options: PropTypes.array,
  extra_params: PropTypes.object,
  container_props: PropTypes.string,
  add_data_path: PropTypes.string,
  edit_data_path: PropTypes.string,
  columns: PropTypes.array.isRequired,
  addable: PropTypes.bool,
  editable: PropTypes.bool,
  deletable: PropTypes.bool,
  selectable: PropTypes.bool,
  support_pagination: PropTypes.bool,
  show_data_count: PropTypes.bool,
  setLoading: PropTypes.func,
};

ConfigurationsTableSection.defaultProps = {
  title: '',
  title_font_size: '',
  title_color: '',
  table_name: '',
  id_field: '',
  user_relation_table_name: '',
  columns_config: [],
  column_mapping_config: [],
  value_mapping_config: {},
  odd_row_background_color: '',
  even_row_background_color: '',
  odd_row_font_color: '',
  even_row_font_color: '',
  header_background_color: '',
  header_font_color: '',
  page_options: [],
  extra_params: {},
  container_props: '',
  add_data_path: '',
  edit_data_path: '',
  addable: true,
  editable: true,
  deletable: true,
  selectable: false,
  support_pagination: false,
  show_data_count: false,
};

export default withLoading(ConfigurationsTableSection);
